import React, { createContext, useContext, useEffect, useState } from "react";
import { AlertTriangle, LoaderCircle, Wrench } from "lucide-react";

import { request } from "@/lib/apiClient";
import { useLocalization } from "@/lib/localization";
import { toWebPlatformConfiguration } from "@/lib/webPlatformConfiguration";

const PlatformConfigurationContext = createContext(null);
const CACHE_KEY = "shopbook.web.platform-configuration";

function cachedConfiguration() {
  try {
    return toWebPlatformConfiguration(JSON.parse(window.localStorage.getItem(CACHE_KEY) || "null"));
  } catch {
    return null;
  }
}

export function PlatformConfigurationProvider({ children }) {
  const { locale } = useLocalization();
  const [configuration, setConfiguration] = useState(cachedConfiguration);
  const [ready, setReady] = useState(Boolean(configuration));

  useEffect(() => {
    let active = true;
    let timer;
    const refresh = async () => {
      try {
        const payload = await request("/api/v1/config/bootstrap?platform=web");
        if (!active || !payload?.configuration) return;
        const webConfiguration = toWebPlatformConfiguration(payload.configuration);
        setConfiguration(webConfiguration);
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(webConfiguration));
      } catch {
        // Fail open with the last known valid configuration.
      } finally {
        if (active) {
          setReady(true);
          timer = window.setTimeout(refresh, 60_000);
        }
      }
    };
    void refresh();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, []);

  if (!ready) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F4F8F5] text-[#0E5B52]"><LoaderCircle className="h-6 w-6 animate-spin" /><span className="ml-2 text-sm">Loading workspace configuration…</span></div>;
  }

  if (configuration?.maintenance?.active && configuration.maintenance.mode === "unavailable") {
    return <MaintenanceScreen locale={locale} maintenance={configuration.maintenance} />;
  }

  return (
    <PlatformConfigurationContext.Provider value={configuration}>
      {configuration?.maintenance?.active && configuration.maintenance.mode === "read_only" && (
        <div role="status" className="flex items-center justify-center gap-2 bg-amber-100 px-4 py-2 text-center text-xs font-medium text-amber-950">
          <AlertTriangle className="h-4 w-4" />{configuration.maintenance.title}: {configuration.maintenance.message}
        </div>
      )}
      {children}
    </PlatformConfigurationContext.Provider>
  );
}

export function usePlatformConfiguration() {
  return useContext(PlatformConfigurationContext);
}

function MaintenanceScreen({ locale, maintenance }) {
  return <main className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,#E4F3EA,#F7FAF8_55%)] p-6"><section className="w-full max-w-lg rounded-3xl border border-[#0E5B52]/10 bg-white p-8 text-center shadow-2xl"><span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-700"><Wrench className="h-6 w-6" /></span><h1 className="mt-5 text-2xl font-semibold text-[#064E3B]">{maintenance.title}</h1><p className="mt-3 text-sm leading-6 text-[#5E6E68]">{maintenance.message}</p>{maintenance.endsAt && <p className="mt-5 rounded-xl bg-[#F4F8F5] p-3 text-xs text-[#53635E]">Expected completion: {new Date(maintenance.endsAt).toLocaleString(locale)}</p>}</section></main>;
}
